import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import useAuth from "src/context/AuthContext";
import { AUTH_ROUTES } from "src/config/constants";
import MPHInput from "src/components/DataProviders/MPHInput";
import MPHButton from "../MPHButtons/MPHButton";
import {
  signUpSchema,
  signUpValidation,
} from "src/utils/MPHFormsSchemaValidations/MPHFormsSchemaValidations";
import { useApiAxiosRequests } from "src/utils/ApiService/MPHApis";
import { errorToast, successToast } from "src/utils/toast";
import { useFormik } from "formik";
import MPHOtp from "../MPHOtp/MPHOtp";
import MPHSignUpProfileUpdate from "../MPHSignUpProfileUpdate/MPHSignUpProfileUpdate";
import { useStorage } from "src/utils/storage";
import "../MPHSignUp/MPHSignUp.scss";

const MPHSignUp = () => {
  const storage = useStorage();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const promotion = searchParams.get('promotion') || '';
  storage.set("promotion", promotion);
  const acquisitionChannel = searchParams.get("acquisition_channel") || "";
  const rewardredeem = searchParams.get('rewardredeem') || 'false';
  const reward = searchParams.get('reward') || '';
  const updatedSignUpSchema = {
    ...signUpSchema,
    identifierValue: email,
  };
  const [isOtp, setIsOtp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isProfileUpdate, setIsProfileUpdate]: any = useState(null);
  const [validateToken, setvalidateToken] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [initialValues] = useState(updatedSignUpSchema);
  const navigate = useNavigate();
  const { setTokens, setUserProfile } = useAuth();
  const { tokenGenerate, otpGenerate, otpValidate, getCustomerByEmailLoggedIn } = useApiAxiosRequests();

  const tokenGenerateFunc = async () => {
    const payload = values;
    payload.identifierValue = payload.identifierValue.toLowerCase();
    const res: any = await tokenGenerate(payload);
    if (res.data && res.data.status && res.data.status.success) {
      setSessionId(res.data.user.sessionId);
    } else {
      if (res.data && res.data.status && res.data.status.message) {
        errorToast(res.data && res.data.status && res.data.status.message);
      }
    }
    return {
      status: res.data && res.data.status && res.data.status.success,
      data: res.data,
    };
  };

  const handleSaveNext = async () => {
    setIsLoading(true);
    const userDetails = await tokenGenerateFunc();
    if (!userDetails.status) {
      setIsLoading(false);
      return;
    }
    if (userDetails.data.user.userRegisteredForPassword) {
      errorToast("This email address already is associated with an existing account. Please log in or use a different email to enroll.");
      setIsLoading(false);
      return;
    }
    if (!userDetails.data.user.userRegisteredForPassword) {
      generateOtpFunc(userDetails.data.user);
      return;
    }
  };

  const generateOtpFunc = async (data: any) => {
    const payload = {
      identifierType: values.identifierType,
      identifierValue: values.identifierValue.toLowerCase(),
      deviceId: values.deviceId,
      brand: values.brand,
      sessionId: data.sessionId,
    };
    const res: any = await otpGenerate(payload);
    setIsLoading(false);

    if (res && res.data && res.data.status && res.data.status.success) {
      successToast(res.data.status.message);
      setIsOtp(true);
    } else {
      errorToast(
        (res && res.data && res.data.status && res.data.status.message) ||
        "Error Generating in verification code, Please try again"
      );
    }
  };

  const otpValidateFunc = async (otp: any) => {
    const payload = {
      identifierType: values.identifierType,
      identifierValue: values.identifierValue.toLowerCase(),
      deviceId: values.deviceId,
      brand: values.brand,
      sessionId: sessionId,
      otp: otp,
    };
    setIsLoading(true);
    const res: any = await otpValidate(payload);
    setIsLoading(false);
    if (res.data && res.data.status && res.data.status.success) {
      if (res.data.auth && res.data.auth.token) {
        const authToken = res.data.auth.token;
        const emailLower = values.identifierValue.toLowerCase();

        // Check if the user already has an Intouch profile before showing the registration form.
        // This covers the case where the user is attempting re-registration after having
        // completed the full signup flow previously via another path.
        const result: any = await getCustomerByEmailLoggedIn({ email: emailLower }, authToken);
        if (
          result?.data?.status?.success === "true" &&
          result?.data?.customers?.customer?.[0]?.type === "LOYALTY"
        ) {
          const customer = result.data.customers.customer[0];
          setTokens(res.data.auth);
          setUserProfile({
            method: "auth",
            previousStep: "",
            token: authToken,
            role: "User",
            isProfileExist: true,
            links: AUTH_ROUTES,
            profileName: customer.firstname || "",
            loyalty_points: customer.loyalty_points || 0,
            email: emailLower,
            external_id: customer.external_id || "",
          });
          navigate("/status");
          return;
        }

        // User not yet in Intouch — proceed to the Registration Page
        setvalidateToken(authToken);
        setIsProfileUpdate(res.data.auth);
      }
    } else {
      errorToast(
        (res && res.data && res.data.status && res.data.status.message) ||
        "Error Validating verification code, Please try again"
      );
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: signUpValidation,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: handleSaveNext,
  });
  const { values, handleChange, handleBlur, errors, touched, isValid, setFieldValue } = formik;

  useEffect(() => {
    const state = location.state as { isDropOut?: boolean; auth?: any; email?: string } | null;
    if (state?.isDropOut && state.auth) {
      setvalidateToken(state.auth.token);
      setIsProfileUpdate(state.auth);
      if (state.email) {
        setFieldValue("identifierValue", state.email);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="d-flex justify-center">
      <div className="signup-modal">
        <div className="modal-dialog modal-dialog-centered">
          {isProfileUpdate ? (
            <>
              <MPHSignUpProfileUpdate
                validateToken ={validateToken} //passing the otp validate token as props
                token={isProfileUpdate}
                email={values.identifierValue}
                promotion={promotion}
                acquisitionChannel={acquisitionChannel}
                rewardredeem={rewardredeem}
                reward={reward}
                close={() => {
                  setIsOtp(false);
                  setIsProfileUpdate(false);
                }}
              />
            </>
          ) : (
            <>
              {!isOtp ? (
                <div className="modal-content form-modal-signup">
                  <div className="row">
                    <section className="mb-3">
                      <h2 className="form-header">Yes, I’d like to join</h2>
                    </section>
                    <div className="col-xl-12 col-lg-12 col-sm-12 col-md-12 mt-2">
                      <label htmlFor="" className="form-label">
                        Email Address
                      </label>
                      <MPHInput
                        keyboardType="email"
                        onBlur={handleBlur("identifierValue")}
                        value={values.identifierValue}
                        onChangeText={(text: any) => {
                          handleChange("identifierValue")(text);
                        }}
                        isError={
                          touched.identifierValue! && !!errors.identifierValue
                        }
                        error={errors.identifierValue}
                        isModal={true}
                      />
                    </div>
                    <div className="col-xl-12 col-lg-12 col-sm-12 col-md-12 mt-2 mb-3">
                      <label htmlFor="" className="form-label">
                        Password
                      </label>
                      <MPHInput
                        keyboardType="password"
                        value={values.password}
                        onBlur={handleBlur("password")}
                        onChangeText={(text: any) => {
                          handleChange("password")(text);
                        }}
                        isError={touched.password! && !!errors.password}
                        error={errors.password}
                        isModal={true}
                      />
                      <span className="password-helper mb-3">
                        The password must be at least 8 characters and contain
                        at least one uppercase character.
                      </span>
                    </div>
                    <div className="col-xl-12 col-lg-12 col-sm-12 col-md-12 mt-2">
                      <label htmlFor="" className="form-label">
                        Confirm Password
                      </label>
                      <MPHInput
                        keyboardType="password"
                        value={values.confirmPassword}
                        onBlur={handleBlur("confirmPassword")}
                        onChangeText={(text: any) => {
                          handleChange("confirmPassword")(text);
                        }}
                        isError={
                          touched.confirmPassword! && !!errors.confirmPassword
                        }
                        error={errors.confirmPassword}
                        isModal={true}
                      />
                    </div>
                    <div className="col-xl-6 col-lg-6 col-sm-12 col-md-12 mt-5">
                      <MPHButton
                        buttonText="Join For Free"
                        disabled={!isValid || isLoading}
                        isLoading={isLoading}
                        onbuttonClick={() => handleSaveNext()}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <MPHOtp
                  text={values.identifierValue}
                  isLoading={isLoading}
                  darkTheme={true}
                  isModal={false}
                  onClose={() => setIsOtp(false)}
                  onOtp={(otp: any) => otpValidateFunc(otp)}
                />
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default MPHSignUp;
